import Entry from 'src/clientRoutes/sign-up'
import { FeatureFlags } from 'src/experiments/featureFlags'
import { dynamicWithFeatureFlag } from 'src/experiments/featureFlags/dynamicWithFeatureFlag'
import { getI18n } from 'src/i18n/i18n.middleware'
import { SSR_DUAL_MODE_EXPERIMENT } from 'src/utils/router/ssrShell'
import { staticMeta, staticMetaLoader } from 'src/utils/ssr-meta/seo'
import type { Route } from './+types/sign-up'

export const handle = {
  name: 'sign-up',
  track: { scene: 'sign-up' },
  // See `routes/login.tsx` for the same setup. `ssrShell: true` tells
  // `src/root.tsx` to render `<Outlet />` directly so the SignPage body
  // (h1/headlines/showcase/how-it-works/tools/features/FAQ/JsonLd) lands
  // in the first SSR flush instead of being replaced with an empty
  // `<!--$--><!--/$-->` placeholder behind RootRuntime's Suspense.
  ssrShell: true,
  // Gate the SSR-shell branch behind the global Stage 2 dual-mode
  // experiment shared with `routes/login.tsx`. See
  // `src/utils/router/ssrShell.ts`.
  ssrShellExperiment: SSR_DUAL_MODE_EXPERIMENT,
}

/** SSR loader. Source: https://github.com/troph-team/pixai/blob/main/packages/pixai-server/src/ssr/router.ts#L80-L86 */
export function loader({ request, context }: Route.LoaderArgs) {
  const { t } = getI18n(context)
  return staticMetaLoader({
    requestUrl: request.url,
    title: t('server:meta.register.title'),
    description: t('server:meta.register.description'),
  })
}

/** Meta function for SSR SEO. */
export function meta({ loaderData }: Route.MetaArgs) {
  return staticMeta(loaderData)
}

/**
 * Dual-mode entry. Same shape as `routes/login.tsx` — both `'off'`
 * and `'on'` render the exact same `<SignPage />` tree (sign-up
 * variant). The only material difference between the branches is
 * whether the SSR-shell branch in `root.tsx` is taken, which is
 * gated by the global `FeatureFlags.SsrDualMode` flag.
 */
export default dynamicWithFeatureFlag({
  flagName: FeatureFlags.SsrDualMode,
  off: () => <Entry />,
  on: () => <Entry />,
})
